'use client';

import React, { memo } from 'react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';

type ChipColor = 'gray' | 'red' | 'yellow' | 'green' | 'blue' | 'indigo' | 'purple' | 'pink';

interface ChipProps {
  children: React.ReactNode;
  color?: ChipColor;
}

const colorClasses: Record<
  ChipColor,
  { light: { bg: string; text: string }; dark: { bg: string; text: string } }
> = {
  gray: {
    light: { bg: 'bg-gray-100', text: 'text-gray-600' },
    dark: { bg: 'bg-gray-400/10', text: 'text-gray-400' },
  },
  red: {
    light: { bg: 'bg-red-100', text: 'text-red-700' },
    dark: { bg: 'bg-red-400/10', text: 'text-red-400' },
  },
  yellow: {
    light: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
    dark: { bg: 'bg-yellow-400/10', text: 'text-yellow-500' },
  },
  green: {
    light: { bg: 'bg-green-100', text: 'text-green-700' },
    dark: { bg: 'bg-green-400/10', text: 'text-green-400' },
  },
  blue: {
    light: { bg: 'bg-blue-100', text: 'text-blue-700' },
    dark: { bg: 'bg-blue-400/10', text: 'text-blue-400' },
  },
  indigo: {
    light: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
    dark: { bg: 'bg-indigo-400/10', text: 'text-indigo-400' },
  },
  purple: {
    light: { bg: 'bg-purple-100', text: 'text-purple-700' },
    dark: { bg: 'bg-purple-400/10', text: 'text-purple-400' },
  },
  pink: {
    light: { bg: 'bg-pink-100', text: 'text-pink-700' },
    dark: { bg: 'bg-pink-400/10', text: 'text-pink-400' },
  },
};

const Chip = memo(function Chip({ children, color = 'gray' }: ChipProps) {
  const { darkMode } = useDarkMode();
  const colors = darkMode ? colorClasses[color].dark : colorClasses[color].light;

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}
    >
      {children}
    </span>
  );
});

export default Chip;
